import { HiOutlineFolder, HiOutlinePencilSquare } from "react-icons/hi2";

import type { FileRecord } from "../entities/files";
import { SourceBadge } from "./FileBadges";
import { pluralize } from "./fileLabels";
import { usageLabel } from "./filesSupport";
import { fileTypeLabel, formatBytes } from "./formatting";

function statusLabel(status: FileRecord["status"]): string {
  return {
    pending: "Загружается",
    ready: "Готов",
    rejected: "Не принят",
    expired: "Срок истёк",
  }[status];
}

/** Сколько живых ссылок держат файл: числом и словом, чтобы «0» не читалось как ошибка. */
function referencesLabel(count: number): string {
  return count === 0
    ? "Ссылок нет"
    : `${count} ${pluralize(count, ["ссылка", "ссылки", "ссылок"])}`;
}

/** Место использования в списке начинается с заглавной: это отдельная строка, а не часть фразы. */
function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export interface FilesTableProps {
  files: readonly FileRecord[];
  canDownload: boolean;
  canDelete: boolean;
  /** Можно ли перекладывать файл: решает организация файла, а не общий флаг. */
  canMove: (file: FileRecord) => boolean;
  /** Папку файла можно открыть, только если она есть в дереве человека. */
  isKnownFolder: (id: string) => boolean;
  busy: { download: boolean; remove: boolean };
  onOpen: (file: FileRecord) => void;
  onDownload: (file: FileRecord) => void;
  onDelete: (file: FileRecord) => void;
  onMove: (file: FileRecord) => void;
  onRename: (file: FileRecord) => void;
  onOpenFolder: (id: string) => void;
  /** Пояснение к файлу без организации: что случится при переносе. */
  moveNote: (file: FileRecord) => string | null;
  /** Перетаскивание файла на папку дерева; работает там, где файл вообще можно переложить. */
  onDragStartFile: (file: FileRecord) => void;
  onDragEnd: () => void;
  draggingFileId: string | null;
}

export function FilesTable({
  files,
  canDownload,
  canDelete,
  canMove,
  isKnownFolder,
  busy,
  onOpen,
  onDownload,
  onDelete,
  onMove,
  onRename,
  onOpenFolder,
  moveNote,
  onDragStartFile,
  onDragEnd,
  draggingFileId,
}: FilesTableProps) {
  return (
    <div className="users-table-wrap">
      <table className="users-table files-table">
        <thead>
          <tr>
            <th>Файл</th>
            <th>Папка и происхождение</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => (
            <tr
              key={file.id}
              // Перетаскивается строка целиком, как на рабочем столе. Только
              // тот файл, который можно переложить: остальным нечего делать.
              draggable={
                file.status === "ready" && canMove(file) ? true : undefined
              }
              data-dragging={draggingFileId === file.id ? "true" : undefined}
              onDragStart={(event) => {
                event.dataTransfer?.setData(
                  "text/plain",
                  file.original_name ?? file.filename,
                );
                if (event.dataTransfer)
                  event.dataTransfer.effectAllowed = "move";
                onDragStartFile(file);
              }}
              onDragEnd={onDragEnd}
            >
              <td data-label="Файл">
                <button
                  className="file-name-button"
                  type="button"
                  disabled={file.status !== "ready" || !canDownload}
                  onClick={() => onOpen(file)}
                >
                  <strong>{file.original_name ?? file.filename}</strong>
                  <small>{fileTypeLabel(file.mime_type)}</small>
                </button>
                <div className="file-meta">
                  <span className={`status-badge status-badge--${file.status}`}>
                    {statusLabel(file.status)}
                  </span>
                  <span className="muted">{formatBytes(file.size)}</span>
                </div>
              </td>
              <td data-label="Папка и происхождение" className="file-place">
                {file.folder_id && file.folder_name ? (
                  isKnownFolder(file.folder_id) ? (
                    <button
                      className="file-place__folder"
                      type="button"
                      title="Показать файлы этой папки"
                      onClick={() => onOpenFolder(file.folder_id!)}
                    >
                      <HiOutlineFolder aria-hidden="true" />
                      <span>{file.folder_name}</span>
                    </button>
                  ) : (
                    <span className="file-place__folder file-place__folder--static">
                      <HiOutlineFolder aria-hidden="true" />
                      <span>{file.folder_name}</span>
                    </span>
                  )
                ) : (
                  <span className="muted">В корне, без папки</span>
                )}
                <div className="file-place__origin">
                  <SourceBadge type={file.origin} />
                  <span className="muted">
                    {referencesLabel(file.reference_count)}
                  </span>
                </div>
                {file.used_in.length > 0 ? (
                  <ul
                    className="file-place__usage"
                    aria-label="Где используется файл"
                  >
                    {file.used_in.map((usage, index) => (
                      <li
                        key={`${usage.kind}:${usage.document_id ?? ""}:${index}`}
                      >
                        {sentenceCase(usageLabel(usage))}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </td>
              <td data-label="Действия">
                <div className="inline-actions">
                  {file.can_rename ? (
                    <button
                      className="button button--text button--compact file-rename-control"
                      type="button"
                      title="Переименовать файл"
                      aria-label="Переименовать файл"
                      onClick={() => onRename(file)}
                    >
                      <HiOutlinePencilSquare aria-hidden="true" />
                    </button>
                  ) : null}
                  {file.status === "ready" ? (
                    <>
                      <button
                        className="button button--secondary button--compact"
                        type="button"
                        disabled={!canDownload}
                        onClick={() => onOpen(file)}
                      >
                        Открыть
                      </button>
                      <button
                        className="button button--text"
                        type="button"
                        disabled={busy.download || !canDownload}
                        onClick={() => onDownload(file)}
                      >
                        Скачать
                      </button>
                      {canMove(file) ? (
                        <button
                          className="button button--text"
                          type="button"
                          onClick={() => onMove(file)}
                        >
                          Переместить
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <span className="muted">Недоступен до проверки</span>
                  )}
                  {canDelete && file.can_delete ? (
                    <button
                      className="button button--text button--danger"
                      type="button"
                      disabled={busy.remove}
                      onClick={() => onDelete(file)}
                    >
                      Удалить
                    </button>
                  ) : null}
                </div>
                {moveNote(file) ? (
                  <p className="muted file-place__locked">{moveNote(file)}</p>
                ) : null}
                {!file.can_delete && file.reference_count > 0 ? (
                  <p className="muted file-place__locked">
                    Занят: удалить нельзя
                  </p>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
